import { Fragment, useCallback, useEffect, useState } from 'react';
import { useAuthStore } from '../../stores';
import { useTranslation } from '../../i18n';
import {
  deleteVectorPackage,
  fetchVectorPackageFiles,
  fetchVectorPackages,
  importGitLibrary,
  resolveAssetUrl,
  uploadVectorPackage,
  type VectorPackageFile,
  type VectorPackageSummary,
} from '../../util/api';
import './Admin.css';

export function VectorPackages() {
  const { t } = useTranslation();
  const { accessToken } = useAuthStore();
  const [packages, setPackages] = useState<VectorPackageSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [name, setName] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [fileInputKey, setFileInputKey] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [gitUrl, setGitUrl] = useState('');
  const [gitBranch, setGitBranch] = useState('main');
  const [gitPath, setGitPath] = useState('kits');
  const [gitFormat, setGitFormat] = useState<'svg' | 'png'>('svg');
  const [importing, setImporting] = useState(false);
  const [notice, setNotice] = useState('');
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [previewId, setPreviewId] = useState<string | null>(null);
  const [previewFiles, setPreviewFiles] = useState<VectorPackageFile[]>([]);
  const [previewLoading, setPreviewLoading] = useState(false);

  const loadPackages = useCallback(async () => {
    setError('');
    try {
      const data = await fetchVectorPackages(accessToken);
      setPackages(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('admin.vectorPackages.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [accessToken, t]);

  useEffect(() => {
    void loadPackages();
  }, [loadPackages]);

  const handleUpload = async (event: React.FormEvent) => {
    event.preventDefault();
    setError('');
    if (!name.trim()) {
      setError(t('admin.vectorPackages.nameRequired'));
      return;
    }
    if (!file) {
      setError(t('admin.vectorPackages.fileRequired'));
      return;
    }

    setUploading(true);
    try {
      await uploadVectorPackage(name.trim(), file, accessToken);
      setName('');
      setFile(null);
      setFileInputKey((key) => key + 1);
      await loadPackages();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('admin.vectorPackages.uploadFailed'));
    } finally {
      setUploading(false);
    }
  };

  const handleGitImport = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!gitUrl.trim()) {
      setError(t('admin.vectorPackages.gitUrlRequired'));
      return;
    }
    setImporting(true);
    setError('');
    setNotice('');
    try {
      const imported = await importGitLibrary(
        {
          url: gitUrl.trim(),
          branch: gitBranch.trim() || 'main',
          path: gitPath.trim(),
          format: gitFormat,
        },
        accessToken,
      );
      setNotice(t('admin.vectorPackages.gitImported', { count: imported.length }));
      await loadPackages();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('admin.vectorPackages.gitFailed'));
    } finally {
      setImporting(false);
    }
  };

  const handleDelete = async (pkg: VectorPackageSummary) => {
    if (!window.confirm(t('admin.vectorPackages.deleteConfirm', { name: pkg.name }))) return;
    setDeletingId(pkg.id);
    setError('');
    try {
      await deleteVectorPackage(pkg.id, accessToken);
      if (previewId === pkg.id) {
        setPreviewId(null);
        setPreviewFiles([]);
      }
      await loadPackages();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('admin.vectorPackages.deleteFailed'));
    } finally {
      setDeletingId(null);
    }
  };

  const togglePreview = async (packageId: string) => {
    if (previewId === packageId) {
      setPreviewId(null);
      setPreviewFiles([]);
      return;
    }
    setPreviewId(packageId);
    setPreviewLoading(true);
    setPreviewFiles([]);
    try {
      const files = await fetchVectorPackageFiles(packageId, accessToken);
      setPreviewFiles(files);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('admin.vectorPackages.loadFailed'));
    } finally {
      setPreviewLoading(false);
    }
  };

  if (loading) {
    return <div className="admin-loading">{t('common.loading')}</div>;
  }

  return (
    <div className="admin-page">
      <header className="admin-page-header">
        <div>
          <h1>{t('admin.vectorPackages.title')}</h1>
          <p>{t('admin.vectorPackages.description')}</p>
        </div>
      </header>

      {error ? <div className="admin-error">{error}</div> : null}
      {notice ? <div className="admin-muted-text">{notice}</div> : null}

      <section className="admin-card">
        <form className="admin-form" onSubmit={(event) => void handleGitImport(event)}>
          <div className="admin-form-row">
            <label className="admin-form-label">
              {t('admin.vectorPackages.gitUrl')}
              <input
                className="admin-form-input"
                value={gitUrl}
                onChange={(event) => setGitUrl(event.target.value)}
                placeholder="https://github.com/org/repo"
                required
              />
            </label>
            <label className="admin-form-label">
              {t('admin.vectorPackages.gitBranch')}
              <input
                className="admin-form-input"
                value={gitBranch}
                onChange={(event) => setGitBranch(event.target.value)}
                maxLength={255}
              />
            </label>
          </div>
          <div className="admin-form-row">
            <label className="admin-form-label">
              {t('admin.vectorPackages.gitPath')}
              <input
                className="admin-form-input"
                value={gitPath}
                onChange={(event) => setGitPath(event.target.value)}
                placeholder="kits"
              />
            </label>
            <label className="admin-form-label">
              {t('admin.vectorPackages.gitFormat')}
              <select
                className="admin-form-input"
                value={gitFormat}
                onChange={(event) => setGitFormat(event.target.value === 'png' ? 'png' : 'svg')}
              >
                <option value="svg">{t('admin.vectorPackages.formatSvg')}</option>
                <option value="png">{t('admin.vectorPackages.formatPng')}</option>
              </select>
            </label>
          </div>
          <p className="admin-muted-text">{t('admin.vectorPackages.gitHint')}</p>
          <div className="admin-form-actions">
            <button className="admin-btn admin-btn-primary" type="submit" disabled={importing || uploading}>
              {importing ? t('admin.vectorPackages.gitImporting') : t('admin.vectorPackages.gitImport')}
            </button>
          </div>
        </form>
      </section>

      <section className="admin-card">
        <form className="admin-form" onSubmit={(event) => void handleUpload(event)}>
          <div className="admin-form-row">
            <label className="admin-form-label">
              {t('admin.vectorPackages.name')}
              <input
                className="admin-form-input"
                value={name}
                onChange={(event) => setName(event.target.value)}
                maxLength={120}
                required
              />
            </label>
            <label className="admin-form-label">
              {t('admin.vectorPackages.file')}
              <input
                key={fileInputKey}
                className="admin-form-input"
                type="file"
                accept=".zip,.svg,.png,.webp,.jpg,.jpeg,.gif,image/svg+xml,application/zip"
                onChange={(event) => setFile(event.target.files?.[0] ?? null)}
              />
            </label>
          </div>
          <p className="admin-muted-text">{t('admin.vectorPackages.fileHint')}</p>
          <div className="admin-form-actions">
            <button className="admin-btn admin-btn-primary" type="submit" disabled={uploading}>
              {uploading ? t('admin.vectorPackages.uploading') : t('admin.vectorPackages.upload')}
            </button>
          </div>
        </form>
      </section>

      <section className="admin-table-section">
        <div className="admin-table-toolbar">
          <h2>
            {t('admin.vectorPackages.title')} ({packages.length})
          </h2>
        </div>
        {packages.length === 0 ? (
          <div className="admin-empty-state">
            <p>{t('admin.vectorPackages.empty')}</p>
          </div>
        ) : (
          <div className="admin-table-container">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>{t('admin.vectorPackages.name')}</th>
                  <th>{t('admin.vectorPackages.file')}</th>
                  <th>{t('admin.vectorPackages.preview')}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {packages.map((pkg) => (
                  <Fragment key={pkg.id}>
                    <tr>
                      <td>
                        <span className="provider-name">{pkg.name}</span>
                      </td>
                      <td>{pkg.id}</td>
                      <td>{t('admin.vectorPackages.files', { count: pkg.fileCount })}</td>
                      <td className="admin-card-actions">
                        <button
                          type="button"
                          className="admin-btn admin-btn-secondary admin-btn-sm"
                          onClick={() => void togglePreview(pkg.id)}
                        >
                          {previewId === pkg.id
                            ? t('admin.vectorPackages.hide')
                            : t('admin.vectorPackages.preview')}
                        </button>
                        <button
                          type="button"
                          className="admin-btn admin-btn-danger admin-btn-sm"
                          disabled={deletingId === pkg.id}
                          onClick={() => void handleDelete(pkg)}
                        >
                          {t('admin.vectorPackages.delete')}
                        </button>
                      </td>
                    </tr>
                    {previewId === pkg.id ? (
                      <tr key={`${pkg.id}-files`} className="admin-table-sub-row">
                        <td colSpan={4}>
                          {previewLoading ? (
                            <p className="admin-muted-text">{t('common.loading')}</p>
                          ) : (
                            <div className="vector-file-grid">
                              {previewFiles.map((item) => (
                                <figure key={item.id}>
                                  <img src={resolveAssetUrl(item.url)} alt={item.name} />
                                  <figcaption>{item.name}</figcaption>
                                </figure>
                              ))}
                            </div>
                          )}
                        </td>
                      </tr>
                    ) : null}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
